import es6Promise from 'es6-promise';
import fetch from 'isomorphic-fetch';
import config from '../config';

import { notifyError } from '../components/Error/ErrorBoundary';

import { getAuthAccessHeader, refreshAccessToken } from '../modules/User/AuthActions';
import { displayErrors } from '../modules/Error/ErrorActions';

es6Promise.polyfill();

// export const API_URL = (typeof window === 'undefined' || process.env.NODE_ENV === 'development')
//     ? process.env.BASE_URL || (`http://localhost:${config.port}/api`)
//     : '/api';

// export const API_URL = `${config.api}/api`;
export const API_URL = `${config.api}`;

// console.log(config, process.env, API_URL);

export const MAX_ATTEMPTS = process.env.NODE_ENV === 'production' ? 3 : 2;

// Every message the API answers with when the session is no longer valid. The same list decides
// whether a call is worth retrying after a refresh and whether the login modal must be shown, so
// the two can never drift apart.
const AUTH_FAIL_REGEX = /(TokenExpired|InvalidTokenUse|TokenNotFound|NoAccessToken|Unauthorized|JWT|AuthRefreshTokenError)/i;

// Returned by the steps that settle their promise themselves, so the end of the chain knows it has
// nothing left to resolve: a held call settles long after its chain has run to the end.
const SETTLED_ELSEWHERE = Symbol('settledElsewhere');

// A refresh in flight is shared: concurrent calls await the same promise instead of each firing
// their own, which used to let them burn their attempts against a token that was about to arrive.
let refreshPromise = null;

// The login modal must react to an expired session wherever the call was made from, so the caller
// is told instead of being trusted to forward the rejection: most catch blocks only log it.
// Listeners are told when the session comes back too, otherwise a modal that was dismissed stays
// armed and pops up again over the application.
const authFailListeners = new Set();

/*
 * Calls stopped by an expired session, waiting to know whether the user logs back in. They are
 * held rather than rejected right away so that logging in through the modal resumes the action the
 * user had asked for, instead of leaving him in front of a screen that never filled in.
 *
 * Only auth failures are held: any other error is the caller's business as before.
 */
const heldCalls = new Set();

/*
 * Whether something is listening that can actually release the held calls. A call is only worth
 * holding while the login modal can be reached: it lives inside a mounted application, and the
 * mounting itself waits on API calls, so holding without a listener hangs the application for good
 * instead of asking the user to log in.
 */
function canHoldCalls() {
    return authFailListeners.size > 0;
}

export function subscribeToAuthFail(listener) {
    authFailListeners.add(listener);
    return () => authFailListeners.delete(listener);
}

/*
 * Replay every call the expired session had stopped. Called once the user is logged back in, with
 * a fresh token already stored, so each call starts over from its first attempt.
 */
export function replayCallsHeldOnAuthFail() {
    const calls = [...heldCalls];
    heldCalls.clear();
    calls.forEach(call => call.replay());
    return calls.length;
}

/*
 * Give up on the held calls and reject them as they would have been without the hold. Called when
 * the user dismisses the modal instead of logging in: a caller waiting on a promise that never
 * settles would keep showing a loader forever.
 */
export function releaseCallsHeldOnAuthFail() {
    const calls = [...heldCalls];
    heldCalls.clear();
    calls.forEach(call => call.abandon());
    return calls.length;
}

export function isAuthFailError(error) {
    const message = (error || {}).message || error;
    return typeof message === 'string' && AUTH_FAIL_REGEX.test(message);
}

function notifyAuthFail(error) {
    authFailListeners.forEach(listener => listener(error));
}

function notifyAuthRecovered() {
    authFailListeners.forEach(listener => listener(null));
}

/*
 * Refresh the access token once for every caller waiting on it. The promise is cleared as soon as
 * it settles so a later expiry can refresh again, and a refresh that resolves without a token is
 * treated as a failure: leaving it as a success used to block every subsequent refresh.
 */
function refreshAccessTokenOnce() {
    if(!refreshPromise) {
        refreshPromise = refreshAccessToken().then(token => {
            refreshPromise = null;
            if(!token) {
                return Promise.reject(new Error('AuthRefreshTokenError'));
            }
            // The session is valid again: nothing is left to ask the user
            notifyAuthRecovered();
            return token;
        }).catch(err => {
            refreshPromise = null;
            return Promise.reject(err);
        });
    }
    return refreshPromise;
}

export default function callApi(endpoint, method = 'get', body = {}, headersContent = {}, options = {}, attempts = 1) {
    const headers = options.useRawHeaders ? headersContent : new Headers(headersContent || {});
    if(!options.useRawHeaders && !options.useRawBody && !headers.get('Content-Type')) {
        headers.append('Content-Type', 'application/json');
    }
    const isJSON = !options.useRawBody && headers.get('Content-Type') === 'application/json';

    return new Promise((resolve, reject) => {
        if(!options.isExternal && !headers.get('Authorization')) {
            headers.append('Authorization', getAuthAccessHeader());
        }

        const apiUrl = options.isExternal ? endpoint : `${API_URL}/${endpoint}`;

        /*
         * An expired session is broadcast rather than only rejected, because most catch blocks
         * merely log the rejection: without the broadcast the login modal never opens. Announced
         * once per call, since a retry that fails in turn is the same dead session and each extra
         * broadcast would stack another login modal on screen.
         *
         * The call is then held instead of being rejected: if the user logs back in, it is replayed
         * and settles as if the session had never expired. It is only rejected once there is
         * nothing left to wait for, so an unauthenticated caller is never left hanging.
         */
        let hasReportedAuthFail = false;
        const holdOnAuthFail = (error, request) => {
            /*
             * Three calls are rejected instead of being held:
             *  - the refresh request itself (`once`), whose failure is what declares the session
             *    dead: holding it would make that verdict wait on a login it has to trigger;
             *  - a call already replayed once, since an expired session that survives the login is
             *    a real failure rather than something a second login would fix;
             *  - a call the caller needs an answer to before the interface exists (`neverHeld`),
             *    typically the startup check: the login modal only lives inside a mounted
             *    application, so holding what the mounting waits for would hang it for good.
             * Nothing is held either while no listener could release it, which covers the same
             * risk for every call made before the application is mounted.
             */
            if(options.once || options.isReplayed || options.neverHeld || !canHoldCalls()) {
                reject(error);
            } else {
                // Held before the failure is announced, never after: a listener that replays the
                // held calls as soon as it hears about it would otherwise find nothing to replay
                heldCalls.add({
                    // Replayed from the first attempt: the token is fresh, so the backoff and the
                    // attempts already spent against the dead session are irrelevant
                    replay: () => callApi(endpoint, method, body, headersContent, { ...(options || {}), isReplayed: true }).then(resolve, reject),
                    abandon: () => reject(error),
                });
            }
            if(!hasReportedAuthFail) {
                hasReportedAuthFail = true;
                notifyCallApiError(error, request);
                // The refresh request stays silent: its failure is what makes the call that asked
                // for it fail in turn, and that call announces the expired session once it is held.
                // Announcing here would open the modal before anything is waiting to be replayed.
                !options.once && notifyAuthFail(error);
            }
            return SETTLED_ELSEWHERE;
        };

        const launcher = () => {
            try {
                const fetchOptions = {
                    headers,
                    method,
                };
                if(!['GET', 'HEAD'].includes(method.toUpperCase()) && body) {
                    fetchOptions.body = options.useRawBody ? body : JSON.stringify(body);
                }
                fetch(apiUrl, fetchOptions)
                .then(response => {
                    if(response) {
                        return (isJSON ? response.json() : Promise.resolve(response.body))
                            .then(result => ({ result, response }))
                            .catch(err => {
                                // notifyCallApiError(err || new Error('JsonParseError'), apiUrl, method, body, headersContent, options, attempts);
                                // reject(response.statusText || new Error('JsonParseError'));
                                return { response, result: response.statusText };
                            });
                    }
                    notifyCallApiError(new Error('FetchApiError'), { apiUrl, method, body, headersContent, options, attempts });
                    reject(new Error('FetchApiError'));
                    return SETTLED_ELSEWHERE;
                })
                .then(res => {
                    const { result, response } = res || {};
                    // console.log('API Response', result, response);
                    if(!response?.ok) {
                        console.log('API Response Error', isJSON, result, response);
                        // return reject(json.message);
                        if(!options.isExternal && isAuthFail(response, result)) {
                            // The refresh call itself (`once`) must not try to refresh again, and a
                            // call that already exhausted its attempts has a dead session: both end
                            // on the same failure so the applicant is asked to log in only once.
                            if(options.once || attempts > MAX_ATTEMPTS) {
                                return holdOnAuthFail(new Error('AuthRefreshTokenError'), { apiUrl, method, body, headersContent, options, attempts, isJSON, response });
                            }
                            return refreshAccessTokenOnce().then(() => {
                                console.log('Recall API', attempts + 1);
                                // The retry settles this promise on its own: letting its rejection
                                // flow down the chain would report the same expired session twice
                                callApi(endpoint, method, body, headersContent, options, attempts + 1).then(resolve, reject);
                                return SETTLED_ELSEWHERE;
                            }, () => holdOnAuthFail(new Error('AuthRefreshTokenError'), { apiUrl, method, body, headersContent, options, attempts, isJSON, response }));
                        }
                        console.log(result);
                        reject(new Error(result?.error || result?.message || response.statusText || 'FetchApiError'));
                        return SETTLED_ELSEWHERE;
                    }
                    return options.isRaw ? response : result;
                })
                .then(res => res !== SETTLED_ELSEWHERE && resolve(res))
                .catch(err => {
                    console.error(err);
                    // Wrapping an Error in an Error would prefix its message with "Error: ", which
                    // then matches no translation and reaches the toaster as raw text
                    const error = err instanceof Error ? err : new Error(err || 'FetchApiError');
                    reject(error);
                    notifyCallApiError(error, { apiUrl, method, body, headersContent, options, attempts });
                    return err;
                });
            } catch (err) {
                console.error('FetchApiError', err);
                notifyCallApiError(err || new Error('FetchApiError'), { apiUrl, method, body, headersContent, options, attempts });
                reject(err || new Error('FetchApiError'));
            }
        };
        setTimeout(launcher, 1000 * (attempts - 1));
    });
}

export function callExternalApi(endpoint, method = 'get', body = {}, headersContent = {}, options = {}) {
    return callApi(endpoint, method, body, headersContent, { ...(options || {}), isExternal: true });
}

export function buildQuery(obj, numPrefix, tempKey) {
    const outputString = [];

    Object.keys(obj).forEach(val => {
        let key = val;

        if(numPrefix && !Number.isNaN(key)) {
            key = numPrefix + key;
        }

        key = encodeURIComponent(key.replace(/[!'()*]/g, escape));
        if(tempKey) {
            key = `${tempKey}[${key}]`;
        }

        if(typeof obj[val] === 'object') {
            const query = buildQuery(obj[val], null, key);
            outputString.push(query);
        } else {
            const value = typeof obj[val] === 'string' ? encodeURIComponent(obj[val].replace(/[!'()*]/g, escape)) : obj[val];
            outputString.push(`${key}=${value}`);
        }
    });

    return outputString.join('&');
}

function isAuthFail(response, json) {
    // `json` is the parsed body, which falls back to the status text and can be a plain string
    const { name, message } = typeof json === 'object' ? (json || {}) : { message: json };
    const isAuthFail = [401].includes(response.status)
        || isAuthFailError(response.statusText)
        || isAuthFailError(name)
        || isAuthFailError(message);
    isAuthFail && console.error('Request Auth Failed', json);
    return isAuthFail;
}

function notifyCallApiError(err, request = {}) {
    // console.log('Notify Error', err, request);
    notifyError(typeof err === 'string' ? new Error(err) : err, { severity: 'error', ...request });
}
